import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ConfirmPopup from '../../../../components/Popups/ConfirmPopup'
import ModalContainer from '../../../../components/ModalContainer/ModelContainer'
import Icon from '../../../../components/Icons'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import FeaturedTable from '../../../../components/FeaturedTable/FeaturedTable'
import { residentTasksDetailsColumns } from '../helpers'
import {
  setSelectedChecklist,
  getResidentTasks,
  selectChecklistsHistoryHasUpdated,
  sendChecklistReminder,
  removeChecklist,
  updateCustomTaskStatus,
} from '../../../../reducers/Organisation/checklistsReducer'
import TextCell from '../components/TextCell/TextCell'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { ZoomFrame } from '@/components/ui/zoomable-image'

const UNCOMPLETED_STATUSES = ['Started', 'Not Started', 'Past Due']

const ResidentTasksDetailsModal = ({
  resident,
  selectedBuilding,
  isChecklistsHistoryUpdated,
  setSelectedChecklist = () => {},
  setSelectedTask = () => {},
  onClose = () => {},
  getResidentTasks = () => {},
  sendChecklistReminder = () => {},
  removeChecklist = () => {},
  updateCustomTaskStatus = () => {},
  fetchHistory = () => {},
}) => {
  const { t } = useTranslation(['checklists-tasks', 'common'])
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState([])
  const [sendGlobalReminderVisibility, setSendGlobalReminderVisibility] = useState(false)
  const [removeTask, setRemoveTask] = useState(null)
  const [remindTask, setRemindTask] = useState(null)
  const { displaySnackbar } = useSnackbar()

  const hasUncompletedStatuses = data.some((task) => UNCOMPLETED_STATUSES.includes(task.status))

  const fetchResidentsTasks = async () => {
    setIsLoading(true)
    try {
      const responseData = await getResidentTasks(resident.id)
      setData(responseData.checklists)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const onDeleteTask = async () => {
    await removeChecklist(removeTask.id)
      .then(() => {
        displaySnackbar(t('checklists-tasks:residentModal.deleteSuccess'))
      })
      .catch(({ message }) => {
        console.error(message)
        displaySnackbar(t('checklists-tasks:residentModal.deleteError'), true)
      })
      .finally(onDeleteFinally)
  }

  const onDeleteFinally = () => {
    fetchResidentsTasks()
    fetchHistory()
    setRemoveTask(null)
  }

  const onShowDetails = (task) => {
    setSelectedTask({
      data: task,
      resident,
    })
  }

  const sendGlobalReminder = async () => {
    const requestData = {
      clientId: resident.id,
      buildingId: selectedBuilding.id,
      isGlobal: true,
    }
    await sendChecklistReminder(requestData)
      .then(() => {
        displaySnackbar(
          t('checklists-tasks:residentModal.globalReminderSuccess', {
            name: resident.userFullName,
          }),
        )
      })
      .catch(({ message }) => {
        console.error(message)
        displaySnackbar(t('checklists-tasks:residentModal.globalReminderError'), true)
      })
      .finally(() => setSendGlobalReminderVisibility(false))
  }

  const sendReminder = async () => {
    const requestData = {
      clientId: resident.id,
      buildingId: selectedBuilding.id,
      checklistId: remindTask.id,
      isGlobal: false,
    }
    await sendChecklistReminder(requestData)
      .then(() => {
        displaySnackbar(
          t('checklists-tasks:residentModal.reminderSuccess', { taskName: remindTask.name }),
        )
      })
      .catch(({ message }) => {
        console.error(message)
        displaySnackbar(t('checklists-tasks:residentModal.reminderError'), true)
      })
      .finally(() => setRemindTask(null))
  }

  const updateTaskStatus = async (task, statusElement) => {
    const updatedData = data.map((element) => {
      if (task.id === element.id) {
        element.status = statusElement.name
      }
      return element
    })
    setData(updatedData)

    await updateCustomTaskStatus(task.id, statusElement.name)
      .then(() => fetchHistory())
      .catch(({ message }) => console.error(message))
  }

  useEffect(() => {
    if (selectedBuilding && resident) {
      fetchResidentsTasks()
    }
  }, [selectedBuilding, resident])

  useEffect(() => {
    if (isChecklistsHistoryUpdated) {
      fetchResidentsTasks()
      fetchHistory()
    }
  }, [isChecklistsHistoryUpdated])

  return (
    <React.Fragment>
      <ModalContainer
        additionalClass={'checklist-modal'}
        onClose={onClose}
        headerTitle={t('checklists-tasks:residentModal.title')}
        headerIcon={<Icon icon="checkList" />}
      >
        <div
          className={`checklist-modal-content resident-tasks-details ${
            isLoading ? 'is-loading' : ''
          }`}
        >
          <div className="resident-tasks-details-head">
            <div>
              <ZoomFrame
                className="zoom-avatar-frame"
                hintSize="2rem"
                src={getAssetLink({ path: 'chitown-avatar/avatar', file: resident.userAvatar })}
                title={resident.userFullName}
              >
                <img
                  src={getAssetLink({ path: 'chitown-avatar/avatar', file: resident.userAvatar })}
                />
              </ZoomFrame>
              <TextCell title={resident.userFullName} text={resident.userEmail} />
              <TextCell
                title={t('checklists-tasks:residentModal.unitNumberLabel')}
                text={resident.clientApartment}
              />
              <TextCell
                title={t('checklists-tasks:residentModal.completedTasksLabel')}
                text={`${resident.completionPercentage}%`}
              />
            </div>
            <div>
              <button
                className="button button-black"
                onClick={() => setSendGlobalReminderVisibility(true)}
                disabled={isLoading || !hasUncompletedStatuses}
              >
                {t('checklists-tasks:residentModal.sendGlobalReminderButton')}
              </button>
            </div>
          </div>
          {!isLoading && (
            <div className={`ctp-checklists-wrapper-main-table-and-footer-container`}>
              <div className="ctp-checklists-wrapper-main-table resident-tasks-table">
                <FeaturedTable
                  columns={residentTasksDetailsColumns({
                    onDeleteTask: (task) => setRemoveTask(task),
                    onShowDetails,
                    setSelectedChecklist,
                    setRemindTask,
                    updateTaskStatus,
                    navigate,
                    timezone: selectedBuilding.timezone,
                    t,
                  })}
                  data={data}
                />
              </div>
            </div>
          )}
        </div>
      </ModalContainer>
      <ConfirmPopup
        title={t('checklists-tasks:shared.cautionTitle')}
        body={t('checklists-tasks:residentModal.globalReminderConfirmBody', {
          name: resident.userFullName,
        })}
        cancelText={t('common:actions.cancel')}
        confirmText={t('checklists-tasks:residentModal.yesSendReminderButton')}
        isOpen={sendGlobalReminderVisibility}
        cancelCallback={() => setSendGlobalReminderVisibility(false)}
        confirmCallback={sendGlobalReminder}
        className={'is-caution'}
      />
      {remindTask && (
        <ConfirmPopup
          title={t('checklists-tasks:shared.cautionTitle')}
          body={t('checklists-tasks:residentModal.reminderConfirmBody', {
            name: resident.userFullName,
            taskName: remindTask.name,
          })}
          cancelText={t('common:actions.cancel')}
          confirmText={t('checklists-tasks:residentModal.yesSendReminderButton')}
          isOpen={!!remindTask}
          cancelCallback={() => setRemindTask(null)}
          confirmCallback={sendReminder}
          className={'is-caution'}
        />
      )}
      {removeTask && (
        <ConfirmPopup
          title={t('checklists-tasks:shared.confirmationTitle')}
          body={t('checklists-tasks:residentModal.deleteConfirmBody', { name: removeTask.name })}
          cancelText={t('common:actions.cancel')}
          confirmText={t('checklists-tasks:residentModal.deleteTaskConfirmButton')}
          isOpen={!!removeTask}
          cancelCallback={() => setRemoveTask(null)}
          confirmCallback={onDeleteTask}
        />
      )}
    </React.Fragment>
  )
}

ResidentTasksDetailsModal.propTypes = {
  resident: PropTypes.object,
  selectedBuilding: PropTypes.object,
  isChecklistsHistoryUpdated: PropTypes.bool,
  setSelectedChecklist: PropTypes.func,
  onClose: PropTypes.func,
  setSelectedTask: PropTypes.func,
  getResidentTasks: PropTypes.func,
  sendChecklistReminder: PropTypes.func,
  removeChecklist: PropTypes.func,
  updateCustomTaskStatus: PropTypes.func,
  fetchHistory: PropTypes.func,
}

export default connect(
  (state) => ({
    isChecklistsHistoryUpdated: selectChecklistsHistoryHasUpdated(state),
  }),
  {
    setSelectedChecklist,
    getResidentTasks,
    sendChecklistReminder,
    removeChecklist,
    updateCustomTaskStatus,
  },
)(ResidentTasksDetailsModal)
